import { useCallback, useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";
import { useAuth } from "@/contexts/AuthContext";
import { notifyLocationPermissionGranted } from "@/hooks/locationPermissionEvents";

const GEOLOCATION_MAX_AGE = 5 * 60 * 1000; // 5 minutes
const GEOLOCATION_TIMEOUT = 10 * 1000; // 10 seconds
const USER_LOCATION_CACHE_KEY = "stasht_user_location";
const USER_LOCATION_CACHE_MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours

interface UserCoordinates {
  lat: number;
  lng: number;
}

interface CachedLocation extends UserCoordinates {
  timestamp: number;
}

export const getCachedUserLocation = (maximumAge = USER_LOCATION_CACHE_MAX_AGE): CachedLocation | null => {
  try {
    const cached = localStorage.getItem(USER_LOCATION_CACHE_KEY);
    if (!cached) return null;
    const { lat, lng, timestamp } = JSON.parse(cached) as CachedLocation;
    if (Date.now() - timestamp > USER_LOCATION_CACHE_MAX_AGE) {
      localStorage.removeItem(USER_LOCATION_CACHE_KEY);
      return null;
    }
    if (
      Number.isFinite(timestamp) &&
      Math.abs(Date.now() - timestamp) <= maximumAge &&
      typeof lat === "number" &&
      typeof lng === "number"
    ) {
      return { lat, lng, timestamp };
    }
    return null;
  } catch {
    return null;
  }
};

/** Reuse a recent observation only while its existing OS/browser permission is granted.
 * This never requests permission or relabels a home/map viewport as current location. */
export async function getAuthorizedCachedUserLocation(
  maximumAge = GEOLOCATION_MAX_AGE,
): Promise<CachedLocation | null> {
  const cached = getCachedUserLocation(maximumAge);
  if (!cached) return null;
  try {
    const granted = Capacitor.isNativePlatform()
      ? (await Geolocation.checkPermissions()).location === "granted"
      : (await navigator.permissions.query({ name: "geolocation" })).state === "granted";
    return granted ? cached : null;
  } catch {
    return null;
  }
}

const setCachedUserLocation = (coords: UserCoordinates) => {
  try {
    localStorage.setItem(
      USER_LOCATION_CACHE_KEY,
      JSON.stringify({ lat: coords.lat, lng: coords.lng, timestamp: Date.now() }),
    );
  } catch {
    // Ignore localStorage errors
  }
};

interface UseUserLocationOptions {
  /** Whether to enable location fetching (default: true) */
  enabled?: boolean;
  /** Whether to request fresh geolocation on mount (default: true) */
  requestOnMount?: boolean;
  /** Whether to use home_coordinates as fallback when geolocation fails (default: true) */
  useHomeFallback?: boolean;
  /** Number of retries before giving up or falling back (default: 1) */
  maxRetries?: number;
}

interface UseUserLocationResult {
  /** User's coordinates (live, cached, or home fallback) */
  userCoordinates: UserCoordinates | null;
  /** Whether location is currently being fetched */
  isLoading: boolean;
  /** Error if geolocation failed */
  error: Error | null;
  /** Whether coordinates are from home_coordinates fallback vs live/cached */
  isUsingHomeLocation: boolean;
  /** Manually trigger a location refresh */
  refetch: () => void;
}

/**
 * Hook to get user's geolocation with caching and home_coordinates fallback.
 * Works on both web (browser geolocation) and mobile (Capacitor Geolocation).
 */
export const useUserLocation = (options: UseUserLocationOptions = {}): UseUserLocationResult => {
  const { enabled = true, requestOnMount = true, useHomeFallback = true, maxRetries = 1 } = options;
  const { user } = useAuth();

  const [userCoordinates, setUserCoordinates] = useState<UserCoordinates | null>(getCachedUserLocation);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isUsingHomeLocation, setIsUsingHomeLocation] = useState(false);

  const requestStateRef = useRef<"idle" | "inflight" | "resolved">("idle");
  const retryCountRef = useRef(0);
  const [retryTrigger, setRetryTrigger] = useState(0);

  const applyHomeFallback = useCallback(() => {
    if (!useHomeFallback) return false;

    // The API serializes stored coordinates as strings ("37.779379").
    const lat = parseFloat(String(user?.home_coordinates?.lat));
    const lng = parseFloat(String(user?.home_coordinates?.lng));
    if (Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
      console.info("[useUserLocation] Using home_coordinates as fallback");
      setUserCoordinates({ lat, lng });
      setIsUsingHomeLocation(true);
      requestStateRef.current = "resolved";
      return true;
    }
    return false;
  }, [useHomeFallback, user?.home_coordinates]);

  const getUserLocation = useCallback(async () => {
    if (requestStateRef.current === "inflight") return;

    requestStateRef.current = "inflight";
    setIsLoading(true);
    setError(null);

    try {
      let latitude: number;
      let longitude: number;

      if (Capacitor.isNativePlatform()) {
        const permissions = await Geolocation.checkPermissions();
        if (permissions.location !== "granted") {
          const newPermissions = await Geolocation.requestPermissions();
          if (newPermissions.location !== "granted") {
            throw new Error("Location permission denied");
          }
        }

        const position = await Geolocation.getCurrentPosition({
          enableHighAccuracy: false,
          maximumAge: GEOLOCATION_MAX_AGE,
          timeout: GEOLOCATION_TIMEOUT,
        });

        latitude = position.coords.latitude;
        longitude = position.coords.longitude;
      } else {
        if (!navigator.geolocation) {
          throw new Error("Geolocation not supported");
        }

        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: false,
            maximumAge: GEOLOCATION_MAX_AGE,
            timeout: GEOLOCATION_TIMEOUT,
          });
        });

        latitude = position.coords.latitude;
        longitude = position.coords.longitude;
      }

      const coords = { lat: latitude, lng: longitude };
      setUserCoordinates(coords);
      setCachedUserLocation(coords);
      notifyLocationPermissionGranted();
      setIsUsingHomeLocation(false);
      requestStateRef.current = "resolved";
      retryCountRef.current = 0;
    } catch (err) {
      const locationError = err instanceof Error ? err : new Error("Failed to get user location");
      console.warn("[useUserLocation] Failed to get location:", locationError);
      requestStateRef.current = "idle";

      // Retry logic
      if (retryCountRef.current < maxRetries) {
        retryCountRef.current += 1;
        setTimeout(() => {
          setRetryTrigger(t => t + 1);
        }, 1500);
      } else {
        // After retries exhausted, try home fallback or set error
        if (!applyHomeFallback()) {
          setError(locationError);
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, [applyHomeFallback, maxRetries]);

  const refetch = useCallback(() => {
    requestStateRef.current = "idle";
    retryCountRef.current = 0;
    setIsUsingHomeLocation(false);
    void getUserLocation();
  }, [getUserLocation]);

  // Fetch location when enabled
  useEffect(() => {
    if (!enabled) return;
    if (requestStateRef.current === "resolved") return;
    if (!requestOnMount) {
      if (!userCoordinates) {
        applyHomeFallback();
      }
      return;
    }

    void getUserLocation();
  }, [enabled, requestOnMount, getUserLocation, retryTrigger, userCoordinates, applyHomeFallback]);

  return {
    userCoordinates,
    isLoading,
    error,
    isUsingHomeLocation,
    refetch,
  };
};
